import { useState } from 'react';
import { istanbulDayString, t } from '@aquabox/shared';
import { useChannels, useLiveTrips, useMe, useVehicleMutations, useVehicles } from '../api/queries';
import { OPEN_TRIP_STATUSES } from '../lib/stale-trip';
import { useToast } from '../components/Toast';
import { ConfirmDialog } from '../components/ConfirmDialog';
import type { Channel, Vehicle } from '../api/types';
import { AbantEmployeesCard } from './AbantEmployeesCard';
import { DayRosterCard } from './DayRosterCard';
import { HayatVehiclesCard } from './Integrations';
import { labelFor } from './PanelFleetCard';
import { usePanelFleet, usePanelFleetSuggestions } from '../api/panel-fleet';

/**
 * Araçlar (`/araclar`, menü "Araçlar").
 *
 * 21.10 kararı: kişiler (ekip) bu ekrandan çıktı; personel `/personel` ekranındadır. Bu ekranda
 * yalnız araçlar, panel araç eşlemeleri ve öneri kartı kalır. Zimmetli şoför araç satırında
 * yalnız GÖSTERİLİR; zimmet değişikliği personel ekranındadır.
 *
 * Araç kartı yalnız plaka, zimmetli şoför, bugünkü sefer durumu (açık/kapandı/yok) ve bugünkü teslimat
 * sayısını gösterir (UX-A kararı); kapasite ve etiket "Ayrıntılar" açılırındadır. Durum ve sayı
 * `useLiveTrips` önbelleğinden gelir (Seferler ekranıyla AYNI kaynak); bu ekran durumu kendi
 * türetmez, yalnız aynı seferleri araca göre toplar.
 */
export function Fleet() {
  const me = useMe();
  // Bugünün görevlendirmesi yalnız yönetim içindir (API de OWNER/MANAGER ister).
  const canManageDay = me.data?.role === 'OWNER' || me.data?.role === 'MANAGER';
  return (
    <div
      style={{
        padding: '28px 32px 36px',
        display: 'flex',
        flexDirection: 'column',
        gap: 24,
      }}
    >
      {canManageDay ? <DayRosterCard /> : null}
      <VehiclesColumn />
      <PanelVehiclesSection />
      <NeighborhoodSuggestionCard />
    </div>
  );
}

// ── Araçlar ──

const EMPTY_VEHICLE = { plate: '', label: '', capacityJugs: '' };

// Form alanlarının görünür etiketi: placeholder tek başına etiket sayılmaz.
const FIELD_LABEL_STYLE = { display: 'flex', flexDirection: 'column', gap: 6 } as const;
const FIELD_LABEL_TEXT_STYLE = { fontSize: 12.5, color: 'var(--text-secondary)' } as const;

function VehiclesColumn() {
  const toast = useToast();
  const list = useVehicles();
  const { create, update, deactivate } = useVehicleMutations();
  const [draft, setDraft] = useState<typeof EMPTY_VEHICLE | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  // Pasifleştirme onayı: yıkıcı işlem, önce ConfirmDialog.
  const [retiring, setRetiring] = useState<Vehicle | null>(null);

  const vehicles = list.data?.items ?? [];
  // Bugünün seferleri (İstanbul günü): durum + teslimat sayısı araç başına tek satırda.
  const live = useLiveTrips();
  const today = istanbulDayString(new Date());
  // Açık sefer gün farkı gözetmeden sayılır (dünden açık kalan sefer de görünür; TodaySummary ile
  // aynı kural). Kapandı durumu ve teslimat sayısı yalnız bugünün seferlerinden gelir.
  const allTrips = (live.data?.items ?? []).filter((trip) => trip.voidedAt === null);
  const tripSummary = (vehicleId: string) => {
    const mine = allTrips.filter((trip) => trip.vehicle.id === vehicleId);
    const open = mine.some((trip) => OPEN_TRIP_STATUSES.includes(trip.status));
    const own = mine.filter(
      (trip) => trip.tripDate.slice(0, 10) === today || OPEN_TRIP_STATUSES.includes(trip.status),
    );
    const deliveries = own.reduce((sum, trip) => sum + trip.deliveryCount, 0);
    return { state: open ? 'open' : own.length > 0 ? 'closed' : 'none', deliveries } as const;
  };

  function submit(): void {
    if (!draft) return;
    const body = {
      plate: draft.plate.trim(),
      label: draft.label.trim() || undefined,
      // Boş bırakılabilir: kapasite bilinmiyorsa sunucunun varsayılanı kalır, 0 yazılmaz.
      capacityJugs: draft.capacityJugs.trim() === '' ? undefined : Number(draft.capacityJugs),
    };
    const done = (): void => {
      setDraft(null);
      setEditing(null);
    };
    if (editing) {
      update.mutate(
        { id: editing, ...body },
        {
          onSuccess: (v) => {
            toast(t('fleet.vehicleUpdated', { plate: v.plate }));
            done();
          },
        },
      );
    } else {
      create.mutate(body, {
        onSuccess: (v) => {
          toast(t('fleet.vehicleAdded', { plate: v.plate }));
          done();
        },
      });
    }
  }

  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <h2 className="h2" style={{ margin: 0 }}>
          {t('fleet.vehicles')}
        </h2>
        <span style={{ flex: 1 }} />
        <button
          className="btn btn-primary"
          onClick={() => {
            setEditing(null);
            setDraft({ ...EMPTY_VEHICLE });
          }}
        >
          {t('fleet.addVehicle')}
        </button>
      </div>

      <p style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6, margin: 0 }}>
        {t('fleet.vehiclesHint')}
      </p>

      {draft && !editing ? (
        <VehicleForm
          draft={draft}
          onChange={setDraft}
          onCancel={() => setDraft(null)}
          onSubmit={submit}
          pending={create.isPending}
          error={create.error}
          submitLabel={t('fleet.saveVehicle')}
        />
      ) : null}

      <div
        className="card"
        style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 4 }}
      >
        {vehicles.length === 0 ? (
          <p style={{ fontSize: 13.5, color: 'var(--text-secondary)', margin: 0 }}>
            {t('fleet.noVehicles')}
          </p>
        ) : null}

        {vehicles.map((v) =>
          editing === v.id && draft ? (
            <VehicleForm
              key={v.id}
              draft={draft}
              onChange={setDraft}
              onCancel={() => {
                setDraft(null);
                setEditing(null);
              }}
              onSubmit={submit}
              pending={update.isPending}
              error={update.error}
              submitLabel={t('common.save')}
            />
          ) : (
            <div
              key={v.id}
              className="rowgrid-stack"
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 78px 78px',
                alignItems: 'center',
                gap: 10,
                minHeight: 64,
                borderTop: '1px solid var(--border)',
                opacity: v.isActive ? 1 : 0.55,
              }}
            >
              <span style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {/* Kart: plaka, zimmetli şoför, bugünkü sefer durumu ve teslimat sayısı. Model/etiket
                    ve kapasite teknik alandır: "Ayrıntılar" açılırında. */}
                <span style={{ fontWeight: 700, fontSize: 15 }}>{v.plate}</span>
                <span style={{ fontWeight: 600, fontSize: 13, color: 'var(--primary)' }}>
                  {v.drivers && v.drivers.length > 0
                    ? v.drivers.map((d) => d.user.fullName).join(', ')
                    : t('fleet.noDriver')}
                </span>
                {live.isSuccess ? (
                  <span
                    data-testid={`vehicle-trip-${v.id}`}
                    style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}
                  >
                    {t(
                      tripSummary(v.id).state === 'open'
                        ? 'fleet.tripOpen'
                        : tripSummary(v.id).state === 'closed'
                          ? 'fleet.tripClosed'
                          : 'fleet.tripNone',
                    )}
                    {tripSummary(v.id).state !== 'none'
                      ? ` · ${t('fleet.todayDeliveries', { n: tripSummary(v.id).deliveries })}`
                      : ''}
                  </span>
                ) : null}
                <details data-testid={`vehicle-details-${v.id}`}>
                  <summary
                    style={{ fontSize: 12, color: 'var(--text-secondary)', cursor: 'pointer' }}
                  >
                    {t('fleet.details')}
                  </summary>
                  <span
                    style={{ display: 'block', fontSize: 12.5, color: 'var(--text-secondary)' }}
                  >
                    {t('fleet.capacity')}: {v.capacityJugs}
                    {v.label ? ` · ${v.label}` : ''}
                  </span>
                </details>
              </span>
              <button
                className="btn"
                style={{ height: 38 }}
                onClick={() => {
                  setEditing(v.id);
                  setDraft({
                    plate: v.plate,
                    label: v.label ?? '',
                    capacityJugs: String(v.capacityJugs),
                  });
                }}
              >
                {t('common.edit')}
              </button>
              {v.isActive ? (
                <button
                  className="btn"
                  style={{ height: 38 }}
                  onClick={() => {
                    // Önceki denemenin hatası yeni onay penceresine taşınmasın.
                    deactivate.reset();
                    setRetiring(v);
                  }}
                >
                  {t('fleet.retire')}
                </button>
              ) : (
                <span style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>
                  {t('fleet.retired')}
                </span>
              )}
            </div>
          ),
        )}
      </div>

      {retiring ? (
        <ConfirmDialog
          title={t('fleet.retire')}
          body={t('fleet.retireConfirm', { plate: retiring.plate })}
          confirmLabel={t('fleet.retire')}
          tone="danger"
          pending={deactivate.isPending}
          error={
            deactivate.isError
              ? ((deactivate.error as { message?: string } | null)?.message ?? t('error.internal'))
              : null
          }
          onCancel={() => setRetiring(null)}
          onConfirm={() =>
            deactivate.mutate(retiring.id, {
              onSuccess: () => {
                toast(t('fleet.vehicleRetired', { plate: retiring.plate }));
                setRetiring(null);
              },
            })
          }
        />
      ) : null}
    </section>
  );
}

function VehicleForm({
  draft,
  onChange,
  onCancel,
  onSubmit,
  pending,
  error,
  submitLabel,
}: {
  draft: typeof EMPTY_VEHICLE;
  onChange: (d: typeof EMPTY_VEHICLE) => void;
  onCancel: () => void;
  onSubmit: () => void;
  pending: boolean;
  error: unknown;
  submitLabel: string;
}) {
  return (
    <div
      className="card"
      style={{
        padding: 16,
        borderColor: 'var(--primary)',
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
      }}
    >
      <div
        className="form-grid"
        style={{ '--form-cols': '1fr 1fr 110px', '--form-gap': '10px' } as React.CSSProperties}
      >
        <label style={FIELD_LABEL_STYLE}>
          <span style={FIELD_LABEL_TEXT_STYLE}>{t('fleet.plateField')}</span>
          <input
            value={draft.plate}
            onChange={(e) => onChange({ ...draft, plate: e.target.value })}
            placeholder={t('fleet.platePlaceholder')}
            style={{ height: 44 }}
          />
        </label>
        <label style={FIELD_LABEL_STYLE}>
          <span style={FIELD_LABEL_TEXT_STYLE}>{t('fleet.labelField')}</span>
          <input
            value={draft.label}
            onChange={(e) => onChange({ ...draft, label: e.target.value })}
            placeholder={t('fleet.labelPlaceholder')}
            style={{ height: 44 }}
          />
        </label>
        <label style={FIELD_LABEL_STYLE}>
          <span style={FIELD_LABEL_TEXT_STYLE}>{t('fleet.capacity')}</span>
          <input
            value={draft.capacityJugs}
            onChange={(e) => onChange({ ...draft, capacityJugs: e.target.value })}
            inputMode="numeric"
            placeholder={t('fleet.capacity')}
            style={{ height: 44, fontVariantNumeric: 'tabular-nums' }}
          />
        </label>
      </div>
      {/* Plaka sunucuda normalize edilir (34abc123 → 34 ABC 123, Türkçe büyük harfle); panel
          kendi kuralını yazmaz ki iki yerde iki farklı plaka biçimi oluşmasın. */}
      <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: 0 }}>
        {t('fleet.plateHint')}
      </p>
      {error ? <ErrorLine error={error} /> : null}
      <div style={{ display: 'flex', gap: 8 }}>
        <button className="btn" onClick={onCancel}>
          {t('common.cancel')}
        </button>
        <button
          className="btn btn-primary"
          disabled={draft.plate.trim().length < 5 || pending}
          onClick={onSubmit}
        >
          {submitLabel}
        </button>
      </div>
    </div>
  );
}

function ErrorLine({ error }: { error: unknown }) {
  const message = (error as { message?: string } | null)?.message;
  return (
    <p style={{ fontSize: 13, color: 'var(--danger)', margin: 0 }}>
      {message && message.length > 0 ? message : t('error.internal')}
    </p>
  );
}

/**
 * Araçlar & Ekip altındaki "Panel araçları" bölümü.
 *
 * Eşleme UI'sı Integrations.tsx'teki MEVCUT kartlardır (`HayatVehiclesCard`,
 * `AbantEmployeesCard`) — mantık kopyalanmadı, aynı bileşenler burada da render edilir
 * (Integrations'taki sekme de kalır, kullanıcı hangisini kullanırsa kullansın aynı
 * `config.vehicleMap`i okur/yazar).
 *
 * Kartların altında `usePanelFleet({ days: 7 })` ile son 7 günün etiket/çalışan başına
 * sipariş sayısı — patron hangi etiketin eşlenmeye değer olduğunu buradan görür. Bu sayaç
 * salt okunur, defter DEĞİŞMEZ.
 */
function PanelVehiclesSection() {
  const channelsQ = useChannels();
  const hasAbant = (channelsQ.data ?? []).some((c: Channel) => c.source === 'ABANT_PANEL');
  const data = usePanelFleet({ days: 7 });
  const groups = data.data?.groups ?? [];

  return (
    <section
      data-testid="panel-vehicles-section"
      style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <h2 className="h2" style={{ margin: 0 }}>
          {t('panelFleet.sectionTitle')}
        </h2>
      </div>
      <p style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6, margin: 0 }}>
        {t('panelFleet.sectionHint')}
      </p>

      <HayatVehiclesCard />
      {hasAbant ? <AbantEmployeesCard /> : null}

      {groups.length > 0 ? (
        <div
          className="card"
          data-testid="panel-vehicles-weekly-counts"
          style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 4 }}
        >
          <strong style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>
            {t('panelFleet.weeklyCountsTitle')}
          </strong>
          {groups.map((g) => {
            const count = g.counts.new + g.counts.progress + g.counts.done + g.counts.cancelled;
            return (
              <div
                key={`${g.source}::${g.label ?? ''}`}
                data-testid={`panel-vehicles-row-${g.source}-${g.label ?? 'null'}`}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 110px',
                  alignItems: 'center',
                  gap: 10,
                  minHeight: 40,
                  borderTop: '1px solid var(--border)',
                  padding: '6px 0',
                }}
              >
                <span style={{ fontWeight: 600, fontSize: 14 }}>{labelFor(g)}</span>
                <span
                  style={{
                    fontSize: 13,
                    textAlign: 'right',
                    fontVariantNumeric: 'tabular-nums',
                    fontWeight: 600,
                  }}
                >
                  {t('panelFleet.weeklyCount', { n: count })}
                </span>
              </div>
            );
          })}
        </div>
      ) : null}

      {data.isError ? (
        <p style={{ fontSize: 13, color: 'var(--danger)', margin: 0 }}>{t('error.internal')}</p>
      ) : null}
    </section>
  );
}

/**
 * Mahalle → panel aracı öneri kartı (Araçlar & Ekip altında).
 *
 * Geçmiş N günde her (kaynak, mahalle) hücresinin en sık araç etiketini tablo olarak
 * gösterir. YALNIZ bilgi — siparişe, sefere ya da panele YAZMAZ (ileride otomatik atama
 * temeli olacak; bu iş öneri GÖSTERİR).
 *
 * Boşsa kart GÖRÜNMEZ (henüz öneri oluşturacak kadar geçmiş yok demektir; patrona gürültü
 * yok).
 */
function NeighborhoodSuggestionCard() {
  const data = usePanelFleetSuggestions({ days: 30 });
  const rows = data.data?.table ?? [];
  if (data.isPending) return null;
  if (rows.length === 0) return null;
  // Kaynak başına grupla — Hayat ve Abant ayrı tablolar; her biri mahalle adına göre sıralı.
  const bySource = new Map<string, typeof rows>();
  for (const r of rows) {
    const arr = bySource.get(r.source) ?? [];
    arr.push(r);
    bySource.set(r.source, arr);
  }
  const sources = Array.from(bySource.keys()).sort();

  return (
    <section
      data-testid="panel-neighborhood-suggestion"
      style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <h2 className="h2" style={{ margin: 0 }}>
          {t('panelFleet.suggest.title', { days: data.data?.days ?? 30 })}
        </h2>
      </div>
      <p style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6, margin: 0 }}>
        {t('panelFleet.suggest.hint')}
      </p>
      {sources.map((source) => {
        const group = (bySource.get(source) ?? [])
          .slice()
          .sort((a, b) => a.neighborhood.localeCompare(b.neighborhood, 'tr'));
        const label =
          source === 'HAYAT_PANEL'
            ? t('panelStage.brand.HAYAT_PANEL')
            : t('panelStage.brand.ABANT_PANEL');
        return (
          <div
            key={source}
            className="card"
            data-testid={`panel-neighborhood-suggestion-${source}`}
            style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 8 }}
          >
            <strong style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{label}</strong>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1.6fr 1.4fr 0.6fr 0.8fr',
                gap: 10,
                padding: '0 0 6px',
                borderBottom: '1px solid var(--border)',
                fontSize: 12,
                color: 'var(--text-secondary)',
                fontWeight: 600,
              }}
            >
              <span>{t('panelFleet.suggest.columnNeighborhood')}</span>
              <span>{t('panelFleet.suggest.columnVehicle')}</span>
              <span style={{ textAlign: 'right' }}>{t('panelFleet.suggest.columnShare')}</span>
              <span style={{ textAlign: 'right' }}>{t('panelFleet.suggest.columnSamples')}</span>
            </div>
            {group.map((r) => (
              <div
                key={`${r.source}::${r.neighborhood}::${r.vehicleLabel}`}
                data-testid={`panel-neighborhood-row-${r.source}-${r.neighborhood}`}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1.6fr 1.4fr 0.6fr 0.8fr',
                  gap: 10,
                  alignItems: 'center',
                  padding: '6px 0',
                  borderTop: '1px solid var(--border)',
                  fontSize: 13,
                }}
              >
                <span style={{ fontWeight: 600 }}>{r.neighborhood}</span>
                <span style={{ color: 'var(--text-secondary)' }}>
                  {r.vehicle ? r.vehicle.plate : (r.displayName ?? r.vehicleLabel)}
                </span>
                <span
                  style={{
                    textAlign: 'right',
                    fontVariantNumeric: 'tabular-nums',
                    fontWeight: 600,
                  }}
                >
                  {t('panelFleet.suggest.sharePct', { pct: r.sharePct })}
                </span>
                <span
                  style={{
                    textAlign: 'right',
                    fontVariantNumeric: 'tabular-nums',
                    color: 'var(--text-secondary)',
                  }}
                  title={t('panelFleet.suggest.shareSummary', {
                    days: data.data?.days ?? 30,
                    total: r.total,
                    pct: r.sharePct,
                  })}
                >
                  {r.samples}/{r.total}
                </span>
              </div>
            ))}
          </div>
        );
      })}
      {data.isError ? (
        <p style={{ fontSize: 13, color: 'var(--danger)', margin: 0 }}>{t('error.internal')}</p>
      ) : null}
    </section>
  );
}
